import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type DeepLinkType = 'product' | 'group' | 'home';

@Component({
  selector: 'app-redirect',
  templateUrl: './redirect.component.html',
  styleUrls: ['./redirect.component.css'],
})
export class RedirectComponent implements OnInit, OnDestroy {
  public targetWebUrl = '';
  public appDeepLink = '';

  public showFallbackUi = true;

  public readonly playStoreUrl =
    'https://play.google.com/store/apps/details?id=com.qpay.share_me&hl=en';
  public readonly appStoreUrl =
    'https://apps.apple.com/bg/app/shareme-co/id6754183501';

  private readonly androidPackageName = 'com.qpay.share_me';

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const target = params.get('target') ?? params.get('redirect') ?? '';
        const parsed = this.parseTarget(target);

        this.targetWebUrl = parsed.webUrl;
        this.appDeepLink = parsed.deepLink;

        if (this.isIOS()) {
          this.showFallbackUi = true;
          return;
        }

        this.startAutoOpenFlow();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openApp(): void {
    if (!this.appDeepLink) { return; }

    const startedAt = Date.now();

    if (this.isAndroid()) {
      window.location.href = this.toAndroidIntentUrl(this.appDeepLink);
    } else {
      window.location.href = this.appDeepLink;
    }

    window.setTimeout(() => {
      const elapsed = Date.now() - startedAt;
      if (document.visibilityState === 'visible' && elapsed >= 900) {
        this.openStore();
      }
    }, 1200);
  }

  public openStore(): void {
    window.location.href = this.isIOS() ? this.appStoreUrl : this.playStoreUrl;
  }

  public copyLink(): void {
    if (!this.targetWebUrl) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.targetWebUrl).catch(() => this.fallbackCopy());
      return;
    }

    this.fallbackCopy();
  }

  private startAutoOpenFlow(): void {
    if (!this.appDeepLink) {
      this.showFallbackUi = true;
      return;
    }

    this.showFallbackUi = false;

    const startedAt = Date.now();
    let appOpened = false;

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        appOpened = true;
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });

    if (this.isAndroid()) {
      window.location.href = this.toAndroidIntentUrl(this.appDeepLink);
    } else {
      window.location.href = this.appDeepLink;
    }

    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);

      const elapsed = Date.now() - startedAt;
      const stillVisible = document.visibilityState === 'visible';

      if (!appOpened && stillVisible && elapsed >= 900) {
        this.showFallbackUi = true;
      }
    }, 1200);
  }

  private parseTarget(rawTarget: string): { webUrl: string; deepLink: string; type: DeepLinkType } {
    const base = window.location.origin;
    const fallback = { webUrl: base, deepLink: `shareme://${window.location.host}/`, type: 'home' as DeepLinkType };

    if (!rawTarget) {
      return fallback;
    }

    try {
      const decoded = decodeURIComponent(rawTarget);
      const u = new URL(decoded);

      const domainHost = u.host || window.location.host;
      const parts = u.pathname.split('/').filter(Boolean);
      const type = (parts[0] ?? '') as DeepLinkType;

      const webUrl = u.protocol === 'shareme:'
        ? `https://${domainHost}${u.pathname}${u.search}${u.hash}`
        : decoded;

      const deepLink = `shareme://${domainHost}${u.pathname}${u.search}${u.hash}`;

      if (type === 'product' && parts[1] && parts[2]) {
        return { webUrl, deepLink, type: 'product' };
      }

      if (type === 'group' && parts[1]) {
        return { webUrl, deepLink, type: 'group' };
      }

      return {
        webUrl,
        deepLink: `shareme://${domainHost}/`,
        type: 'home',
      };
    } catch {
      return fallback;
    }
  }

  private toAndroidIntentUrl(deepLink: string): string {
    const withoutScheme = deepLink.replace(/^shareme:\/\//i, '');
    const fallback = encodeURIComponent(this.targetWebUrl || this.playStoreUrl);

    return `intent://${withoutScheme}#Intent;scheme=shareme;package=${this.androidPackageName};S.browser_fallback_url=${fallback};end`;
  }

  private fallbackCopy(): void {
    const el = document.createElement('textarea');
    el.value = this.targetWebUrl;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/i.test(navigator.userAgent);
  }

  private isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent);
  }
}
